import {
  Tooltip,
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { memo } from 'react';
import { Close, EmailOutlined, HelpOutline } from '@mui/icons-material';
import { getColor } from '../../../../../assets/theme';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import InputFieldsController from '../../../../../components/form/controller/input-fields.controller';
import AutoCompleteController from '../../../../../components/form/controller/autocomplete.controller';
import { profileAddEmailSchema } from '../../../interface/fo-profile-schema';
import { useSelector } from 'react-redux';
import useToast from '../../../../../hooks/useToast';
import { RootState } from '../../../../../store';
import { useUpdateContactInformationsMutation } from '../../../../../api/front-office/profile.api.rtk';
import { SHA512 } from 'crypto-js';
import { ModalWrapper } from '../../../../../hooks/useModal';

export interface AddEmail {
  email: string;
  primary: string;
}

const AddEmailModal = ({
  isOpen,
  onClose,
  profileID,
}: {
  isOpen: boolean;
  onClose: () => void;
  profileID?: string | null | undefined;
}) => {
  const selectedPropertyID = useSelector(
    (state: RootState) => state.properties.selectedProperty.value,
  );
  const { toast } = useToast();
  const { primary, textGray } = getColor();
  const optionsPrimary = ['Yes', 'No'];
  const [updateContactInformations] = useUpdateContactInformationsMutation();

  const handleDrawerClose = () => {
    onClose();
  };

  const handleAddEmail: SubmitHandler<AddEmail> = async (data) => {
    const { email, primary } = data;

    const emailAdd = email;
    const CIType = 'email';
    const isPrimary = primary === 'Yes';
    const action = 'update';
    const contactInformationsLength = 1;

    const signature = SHA512(
      selectedPropertyID +
        profileID +
        CIType +
        emailAdd +
        +!!isPrimary +
        action,
    ).toString();

    const profileSignature = SHA512(
      selectedPropertyID + profileID + contactInformationsLength,
    ).toString();

    const emailBody = {
      properties: [
        {
          property: {
            propertyID: selectedPropertyID,
            profiles: [
              {
                profile: {
                  profileID: profileID,
                  contactInformations: [
                    {
                      contactInformation: {
                        CIType: CIType,
                        value: emailAdd,
                        isPrimary: isPrimary,
                        action: 'update',
                        signature: signature,
                      },
                    },
                  ],
                  signature: profileSignature,
                },
              },
            ],
          },
        },
      ],
    };

    await updateContactInformations(emailBody)
      .unwrap()
      .then(() => {
        toast({
          severity: 'success',
          message: 'Email Address added successfully!',
        });
        reset();
        handleDrawerClose();
      })
      .catch((err) => {
        toast({
          severity: 'error',
          message: err,
        });
      });
  };

  const methods = useForm<AddEmail>({
    defaultValues: {
      email: '',
      primary: '',
    },
    resolver: yupResolver<AddEmail>(profileAddEmailSchema),
    mode: 'onSubmit',
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  return (
    <ModalWrapper
      hideBackdrop
      disableOnClose
      onClose={onClose}
      modalDisclosure={{
        isOpen: isOpen,
        onClose: onClose,
        onToggle: () => {},
        onOpen: () => {},
      }}
      boxStyle={{
        width: '398px',
        height: '300px',
        maxHeight: '90vh',
      }}
    >
      <Box p={3} sx={{ position: 'relative' }}>
        <form id='add-email' onSubmit={handleSubmit(handleAddEmail)}>
          <Stack
            direction='row'
            justifyContent='space-between'
            borderBottom='1px solid #EEE'
            p='10px 0'
          >
            <Typography
              variant='h5'
              component='div'
              sx={{ flexGrow: 1, color: '#000000' }}
            >
              Add Email
              <Tooltip title='Learn More' placement='right-end'>
                <IconButton
                  sx={{
                    border: 0,
                    color: textGray,
                    bgcolor: 'transparent',
                    ':hover': {
                      color: primary,
                      bgcolor: 'transparent',
                    },
                  }}
                  disableRipple
                >
                  <HelpOutline />
                </IconButton>
              </Tooltip>
            </Typography>
            <IconButton color='inherit' onClick={handleDrawerClose}>
              <Close />
            </IconButton>
          </Stack>
          <Box mb={2} />
          <InputFieldsController
            label='Email'
            name='email'
            control={control}
            errors={errors}
            size='medium'
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <EmailOutlined />
                </InputAdornment>
              ),
            }}
            borderErrorOnly
          />
          <AutoCompleteController
            control={control}
            errors={errors}
            label='Is Primary'
            name='primary'
            options={optionsPrimary || []}
            size='medium'
            borderErrorOnly
          />
        </form>
        <Stack
          direction='row'
          justifyContent='flex-end'
          borderBottom='1px solid #EEE'
        ></Stack>
        <Box mb={2} />
        <Stack direction='row' gap={1} justifyContent='flex-end'>
          <Box flexGrow={1} />
          <Button
            variant='outlined'
            sx={{
              width: '74px',
              height: '36px',
              border: '1px solid',
              backgroundColor: 'transparent',
            }}
            onClick={handleDrawerClose}
          >
            Close
          </Button>
          <Button
            form='add-email'
            type='button'
            variant='contained'
            sx={{
              width: '74px',
              height: '36px',
            }}
            onClick={handleSubmit(handleAddEmail)}
          >
            Save
          </Button>
        </Stack>
      </Box>
    </ModalWrapper>
  );
};

export default memo(AddEmailModal);
